import type { ReelJobEvent } from '../../../shared/api/reels.api';

export type OverallStatusPhase = 'processing' | 'uploading' | 'remuxing' | 'publishing';

export interface ServerProgressStatus {
  phase: OverallStatusPhase;
  current: number;
  total: number;
  percent: number;
}

// Server pipeline progress budget across the whole job:
//   upload  0..40%  (XHR request-body bytes — onUploadProgress)
//   render  40..90% (SSE render% streamed live from ffmpeg)
//   publish 90..100% (per-account completion)

// Total publish attempts the backend makes on an IG-side transient failure
// (1 original + 2 retries). Keep in sync with publish.service IG_TRANSIENT_MAX_ATTEMPTS.
const IG_TOTAL_ATTEMPTS = 3;

// In-window notice shown when the backend auto-retries after an Instagram-side
// transient publish error. Tells the user the failure was on Instagram's side
// and that another attempt is underway.
export function retryNoticeMessage(attempt: number | undefined): string {
  const n = attempt ?? IG_TOTAL_ATTEMPTS;
  return `Ошибка на стороне Instagram. Пробую ещё одну попытку (${n} из ${IG_TOTAL_ATTEMPTS})…`;
}

export function uploadProgressToStatus(percent: number, total: number): ServerProgressStatus {
  return { phase: 'uploading', current: 1, total, percent: Math.round(percent * 0.4) };
}

// `publishedCount` is the count AFTER incrementing for the publish account event.
// Returns null if the event has no bar impact (e.g. publish without accountId, error, malformed).
export function serverEventToStatus(
  event: ReelJobEvent,
  ctx: { total: number; publishedCount: number },
): ServerProgressStatus | null {
  const total = Math.max(1, ctx.total);
  if (event.phase === 'job') return null;
  if (event.phase === 'upload') {
    // Backend sends upload:100 once after the full body has been read.
    return { phase: 'remuxing', current: 1, total, percent: 40 };
  }
  if (event.phase === 'render') {
    return { phase: 'remuxing', current: 1, total, percent: 40 + Math.round(event.percent * 0.5) };
  }
  if (event.phase === 'publish') {
    // publish-without-accountId (publish:0) marks start of IG container processing;
    // server then sits silent for ~30-120s while waitForContainerReady polls IG, so
    // we must flip the bar to 'publishing' here, not on the per-account events only.
    if (!event.accountId) {
      return { phase: 'publishing', current: 1, total, percent: 90 };
    }
    return { phase: 'publishing', current: 1, total, percent: 90 + Math.round((ctx.publishedCount / total) * 10) };
  }
  if (event.phase === 'done') {
    return { phase: 'publishing', current: 1, total, percent: 100 };
  }
  return null;
}
